import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES, GENDERS } from '@stoplord/shared';
import { employeesApi, orgApi } from '@/services/hrm';
import { accessApi } from '@/services/admin';
import type { Employee } from '@/services/types';
import { toast } from '@/store/ui';
import { useCan, useTerms } from '@/hooks/usePermission';
import { humanize, toLocalInput } from '@/lib/format';
import { Button, Checkbox, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { compact, handleFormError } from '../shared/forms';

const optEmail = z.union([z.literal(''), z.string().email('Invalid email')]).optional();
const schema = z
  .object({
    firstName: z.string().trim().min(1, 'Required'),
    lastName: z.string().optional(),
    employeeCode: z.string().optional(),
    officialEmail: optEmail,
    personalEmail: optEmail,
    phone: z.string().optional(),
    gender: z.string().optional(),
    dateOfBirth: z.string().optional(),
    dateOfJoining: z.string().min(1, 'Required'),
    branchId: z.string().optional(),
    departmentId: z.string().optional(),
    designationId: z.string().optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES),
    status: z.enum(EMPLOYEE_STATUSES),
    probationEndsOn: z.string().optional(),
    emergencyName: z.string().optional(),
    emergencyPhone: z.string().optional(),
    notes: z.string().optional(),
    createLogin: z.boolean(),
    loginEmail: optEmail,
    temporaryPassword: z.string().optional(),
    roleId: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.createLogin) return;
    if (!v.loginEmail) ctx.addIssue({ code: 'custom', path: ['loginEmail'], message: 'Login email is required' });
    if (!v.temporaryPassword || v.temporaryPassword.length < 10 || !/\d/.test(v.temporaryPassword) || !/[a-z]/i.test(v.temporaryPassword)) ctx.addIssue({ code: 'custom', path: ['temporaryPassword'], message: 'At least 10 characters with a letter and a number' });
    if (!v.roleId) ctx.addIssue({ code: 'custom', path: ['roleId'], message: 'Choose a role' });
  });
type Form = z.infer<typeof schema>;

export function EmployeeFormModal({ open, onClose, employee }: { open: boolean; onClose: () => void; employee?: Employee }) {
  const terms = useTerms();
  const can = useCan();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!employee;
  const org = useQuery({ queryKey: ['org'], queryFn: orgApi.get, enabled: open });
  const roles = useQuery({ queryKey: ['roles'], queryFn: accessApi.roles, enabled: open && can('access.users.manage') });
  const { register, handleSubmit, reset, watch, formState, setError } = useForm<Form>({ resolver: zodResolver(schema) });
  const createLogin = watch('createLogin');
  const dept = watch('departmentId');

  useEffect(() => {
    if (!open) return;
    reset({
      firstName: employee?.firstName ?? '', lastName: employee?.lastName ?? '', employeeCode: employee?.employeeCode ?? '',
      officialEmail: employee?.officialEmail ?? '', personalEmail: employee?.personalEmail ?? '', phone: employee?.phone ?? '',
      gender: employee?.gender ?? '', dateOfBirth: toLocalInput(employee?.dateOfBirth, false), dateOfJoining: toLocalInput(employee?.dateOfJoining ?? new Date(), false),
      branchId: employee?.branchId ?? '', departmentId: employee?.departmentId ?? '', designationId: employee?.designationId ?? '',
      employmentType: (employee?.employmentType as Form['employmentType']) ?? 'full_time', status: (employee?.status as Form['status']) ?? 'active',
      probationEndsOn: toLocalInput(employee?.probationEndsOn, false), emergencyName: employee?.emergencyContact?.name ?? '', emergencyPhone: employee?.emergencyContact?.phone ?? '',
      notes: employee?.notes ?? '', createLogin: false, loginEmail: '', temporaryPassword: '', roleId: '',
    });
  }, [open, employee, reset]);

  const mut = useMutation({
    mutationFn: (v: Form) => {
      const body = compact({
        firstName: v.firstName, lastName: v.lastName, employeeCode: editing ? undefined : v.employeeCode, officialEmail: v.officialEmail, personalEmail: v.personalEmail, phone: v.phone,
        gender: (v.gender || undefined) as (typeof GENDERS)[number] | undefined,
        dateOfBirth: v.dateOfBirth || undefined, dateOfJoining: v.dateOfJoining, branchId: v.branchId, departmentId: v.departmentId, designationId: v.designationId,
        employmentType: v.employmentType, status: v.status, probationEndsOn: v.probationEndsOn || undefined, notes: v.notes,
        emergencyContact: { name: v.emergencyName || null, phone: v.emergencyPhone || null },
      });
      if (editing) return employeesApi.update(employee!._id, body);
      return employeesApi.create({ ...body, dateOfJoining: v.dateOfJoining, account: v.createLogin ? { email: v.loginEmail!, temporaryPassword: v.temporaryPassword!, roleIds: [v.roleId!] } : undefined });
    },
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      toast.success(editing ? `${terms.employee.singular} updated` : `${terms.employee.singular} added`, e.employeeCode);
      onClose();
      if (!editing) navigate(`/hrm/employees/${e._id}`);
    },
  });

  const designations = org.data?.designations.filter((d) => !dept || !d.departmentId || d.departmentId === dept) ?? [];
  return (
    <Modal open={open} onClose={onClose} size="xl" title={editing ? `Edit ${employee!.displayName}` : `Add ${terms.employee.singular.toLowerCase()}`} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={mut.isPending} onClick={handleSubmit((v) => mut.mutateAsync(v).catch((e) => handleFormError(e, setError)))}>{editing ? 'Save changes' : 'Add'}</Button></>}>
      <form className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field label="First name" required error={formState.errors.firstName?.message}>{(p) => <Input {...p} {...register('firstName')} />}</Field>
        <Field label="Last name">{(p) => <Input {...p} {...register('lastName')} />}</Field>
        <Field label="Employee ID" hint={editing ? undefined : 'Leave empty to auto-generate'}>{(p) => <Input {...p} disabled={editing} {...register('employeeCode')} />}</Field>
        <Field label="Official email" error={formState.errors.officialEmail?.message}>{(p) => <Input {...p} type="email" {...register('officialEmail')} />}</Field>
        <Field label="Personal email" error={formState.errors.personalEmail?.message}>{(p) => <Input {...p} type="email" {...register('personalEmail')} />}</Field>
        <Field label="Mobile" error={formState.errors.phone?.message}>{(p) => <Input {...p} type="tel" {...register('phone')} />}</Field>
        <Field label="Date of joining" required error={formState.errors.dateOfJoining?.message}>{(p) => <Input {...p} type="date" {...register('dateOfJoining')} />}</Field>
        <Field label="Date of birth">{(p) => <Input {...p} type="date" {...register('dateOfBirth')} />}</Field>
        <Field label="Gender">{(p) => <Select {...p} {...register('gender')}><option value="">Not specified</option>{GENDERS.map((g) => <option key={g} value={g}>{humanize(g)}</option>)}</Select>}</Field>
        <Field label="Branch">{(p) => <Select {...p} {...register('branchId')}><option value="">—</option>{org.data?.branches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}</Select>}</Field>
        <Field label="Department">{(p) => <Select {...p} {...register('departmentId')}><option value="">—</option>{org.data?.departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}</Select>}</Field>
        <Field label="Designation">{(p) => <Select {...p} {...register('designationId')}><option value="">—</option>{designations.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}</Select>}</Field>
        <Field label="Employment type">{(p) => <Select {...p} {...register('employmentType')}>{EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        <Field label="Status">{(p) => <Select {...p} {...register('status')}>{EMPLOYEE_STATUSES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}</Select>}</Field>
        <Field label="Probation ends">{(p) => <Input {...p} type="date" {...register('probationEndsOn')} />}</Field>
        <Field label="Emergency contact">{(p) => <Input {...p} {...register('emergencyName')} />}</Field>
        <Field label="Emergency phone">{(p) => <Input {...p} type="tel" {...register('emergencyPhone')} />}</Field>
        <Field label="Notes" className="sm:col-span-2 lg:col-span-3">{(p) => <Textarea {...p} rows={2} {...register('notes')} />}</Field>
        {!editing && can('access.users.manage') && (
          <div className="rounded-lg border border-line p-4 sm:col-span-2 lg:col-span-3">
            <Checkbox label="Create a StopLord login for this person" {...register('createLogin')} />
            {createLogin && (
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <Field label="Login email" required error={formState.errors.loginEmail?.message}>{(p) => <Input {...p} type="email" {...register('loginEmail')} />}</Field>
                <Field label="Temporary password" required error={formState.errors.temporaryPassword?.message} hint="They must change it at first sign-in.">{(p) => <Input {...p} type="text" autoComplete="off" {...register('temporaryPassword')} />}</Field>
                <Field label="Role" required error={formState.errors.roleId?.message}>{(p) => <Select {...p} {...register('roleId')}><option value="">Choose…</option>{roles.data?.filter((r) => !r.allPermissions).map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}</Select>}</Field>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}
